import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useReducedMotion } from 'framer-motion';
import {
  Upload,
  FileText,
  X,
  ArrowRight,
  Lock,
  AlertTriangle,
  MapPin,
} from 'lucide-react';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import FadeIn from '../../components/animations/FadeIn';
import { analyzeMarketQuote, fmtMoney } from '../../lib/quoteCheck';
import VerdictBanner from './components/VerdictBanner';
import CheckupStrip from './components/CheckupStrip';
import MathCard from './components/MathCard';
import EssentialsCard from './components/EssentialsCard';
import PriceCard from './components/PriceCard';
import FlagsCard from './components/FlagsCard';
import PositivesCard from './components/PositivesCard';
import ShareCard from './components/ShareCard';
import NudgeCard from './components/NudgeCard';
import ClaimCard from './components/ClaimCard';
import DisclaimerFooter from './components/DisclaimerFooter';

// /compare/quote — QUOTE CHECK: the public "super-analysis" report card.
//
// The visitor uploads ANY roofing quote (PDF/JPG/PNG, <=10MB, optional
// address) and POST {API_BASE}/api/v1/quote-comparison/market returns the
// deterministic checkup — re-run math, the 12 essentials, red-flag questions,
// mandatory positives, and a market benchmark — rendered here as the report
// card (spec §5): verdict banner → checkup strip → math → essentials → price
// → worth-asking-about → what's right → nudge + bias disclosure → claim card
// → disclaimer. A FAIR quote gets told it's fair, enthusiastically — honesty
// is the product. The report is NEVER walled behind contact info.
//
// Upgraded in place 2026-07 from the sample-baseline comparison page (the
// TAMKO side-by-side) per the Quote Check PRD — same route, same design
// system: Tailwind tokens (primary #07B0E9, ink #0a1628), Lexend type,
// floating Header + Footer, FadeIn reveals, lucide icons. Mobile-first: all
// states verified at 390px with no horizontal overflow; tap targets >= 44px.
//
// COPY CANON: the fee is always "OpenbooQ Service & Warranty fee" — never
// "platform fee" / bare "Service Fee"; timing is "about 30 minutes" — never
// "instant". Verdict-bearing copy is SERVER-composed (counsel-clearable
// templates + banned-lexicon filter) — this page renders it verbatim.

// GTM: the container (GTM-PJH62Z6Q) is loaded globally in index.html and
// owns window.dataLayer; pages push events onto it directly.
function pushDataLayer(event, params = {}) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
}

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

// QA W1.1: phone pickers and file managers hand over files with a blank or
// generic MIME type more often than desktop browsers do — validate by
// extension as the fallback so a legit PDF/JPG/PNG never bounces, and so an
// unsupported pick (HEIC, GIF, …) gets a VISIBLE error instead of silence.
const EXT_TO_MIME = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
};

const UNSUPPORTED_TYPE_MSG =
  "That file type isn't supported — upload a PDF, JPG, or PNG (up to 10 MB).";

// Best-effort media-type resolution: trust a recognizable MIME type first
// (normalizing the nonstandard image/jpg), fall back to the extension when
// the type is missing/generic, and return null for anything unsupported.
// The resolved value is what we send the backend as mediaType.
function resolveMediaType(f) {
  const t = String(f.type || '').toLowerCase();
  if (t === 'image/jpg') return 'image/jpeg';
  if (ACCEPTED_TYPES.includes(t)) return t;
  if (!t || t === 'application/octet-stream') {
    const ext = String(f.name || '').split('.').pop().toLowerCase();
    return EXT_TO_MIME[ext] || null;
  }
  return null;
}

function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1]);
    r.onerror = () => reject(new Error('Failed to read file'));
    r.readAsDataURL(file);
  });
}

// Best-effort city readout from the typed address for the analyzing status
// line ("Comparing against {metro} prices…") — display-only; the real metro
// resolution happens server-side.
function cityFromAddress(address) {
  if (!address) return null;
  const parts = String(address).split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return null;
  const candidate = parts[1];
  if (!candidate || candidate.length < 3 || /\d/.test(candidate)) return null;
  return candidate;
}

// The five analyzing statuses (spec §5). Advances on a rotating cadence and
// HOLDS on the last line (the narrative is sequential — looping back to
// "Reading the quote…" would read as a restart).
function analyzingStatuses(address) {
  const city = cityFromAddress(address);
  return [
    'Reading the quote…',
    'Re-running their math…',
    'Checking the 12 essentials…',
    `Comparing against ${city || 'local market'} prices…`,
    'Writing up what we found…',
  ];
}

const STATUS_CADENCE_MS = 9000;

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

const Reveal = ({ children, delay = 0, className = '' }) => {
  // Honor prefers-reduced-motion: render content statically, no transform.
  // amount=0.05: report cards are tall on phones — FadeIn's default 0.2
  // threshold would keep them invisible until 20% had scrolled in.
  const reduceMotion = useReducedMotion();
  if (reduceMotion) return <div className={className}>{children}</div>;
  return (
    <FadeIn delay={delay} amount={0.05} className={className}>
      {children}
    </FadeIn>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const CompareQuotePage = () => {
  const reduceMotion = useReducedMotion();
  const [file, setFile] = useState(null);
  const [fileMediaType, setFileMediaType] = useState(null);
  const [address, setAddress] = useState('');
  const [phase, setPhase] = useState('empty'); // 'empty' | 'uploaded' | 'analyzing'
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState(null);
  const [statusIdx, setStatusIdx] = useState(0);
  const [statuses, setStatuses] = useState(() => analyzingStatuses(''));
  const fileInputRef = useRef(null);
  const resultRef = useRef(null);
  const errorRef = useRef(null);

  // QA W1.1: flag the route on <body> so global CSS (styles/tailwind.css) can
  // suppress the floating widgets (headset bubble + Chatbase) at phone widths
  // on THIS route only — they overlap the address field, verdict, and claim
  // CTA at 390px. The floaters mount outside the router (App.jsx siblings)
  // and Chatbase is a third-party embed, so a body class is the one hook that
  // reaches both.
  useEffect(() => {
    document.body.classList.add('qc-quote-check');
    return () => document.body.classList.remove('qc-quote-check');
  }, []);

  // Advance the analyzing status line while the backend works; hold on the
  // last line rather than looping.
  useEffect(() => {
    if (phase !== 'analyzing') return undefined;
    setStatusIdx(0);
    const id = window.setInterval(
      () => setStatusIdx((i) => Math.min(i + 1, statuses.length - 1)),
      STATUS_CADENCE_MS
    );
    return () => window.clearInterval(id);
  }, [phase, statuses]);

  // Scroll to the report once it renders.
  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    }
  }, [result, reduceMotion]);

  // QA W1.1: make failures impossible to miss on phones — the error banner
  // sits below the tall dropzone, which can be off-screen at 390px when the
  // file picker closes, so a rejection looked like "nothing happened".
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'nearest',
      });
    }
  }, [error, reduceMotion]);

  const handleFile = (f) => {
    if (!f) return;
    const mediaType = resolveMediaType(f);
    if (!mediaType) {
      setError(UNSUPPORTED_TYPE_MSG);
      // Reset the input so re-picking the SAME file fires onChange again —
      // otherwise the second attempt is silent even with the banner wired.
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setError('File is larger than 10 MB. Please try a smaller file.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setFile(f);
    setFileMediaType(mediaType);
    setPhase('uploaded');
    setError('');
  };

  const clearFile = () => {
    setFile(null);
    setFileMediaType(null);
    setPhase('empty');
    setResult(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const analyze = async () => {
    if (!file || phase === 'analyzing') return;
    setError('');
    setResult(null);
    setStatuses(analyzingStatuses(address));
    setPhase('analyzing');
    pushDataLayer('quote_check_started', { has_address: Boolean(address.trim()) });

    try {
      const fileBase64 = await fileToBase64(file);
      // 90s AbortController + no auto-retry + per-status error copy +
      // render-safe normalization all live in lib/quoteCheck.js.
      const report = await analyzeMarketQuote({
        fileBase64,
        // Resolved at selection time (extension fallback for blank/generic
        // phone-picker MIME types) — file.type alone can be '' on mobile.
        mediaType: fileMediaType || file.type,
        address,
      });
      setResult(report);
      setPhase('uploaded');
      pushDataLayer('quote_check_completed', {
        verdict: report.verdict,
        competitor_total: report.analysis.statedTotal?.value || 0,
        has_our_estimate: Boolean(report.benchmark.ourEstimate),
      });
    } catch (err) {
      setPhase('uploaded');
      setError(err?.message || 'Something went wrong analyzing the file. Please try again.');
    }
  };

  // ----- Derived report values (all null-tolerant via the normalizer) -----
  const contractorName = result?.analysis?.contractorName || 'Unknown contractor';
  const statedTotal = result?.analysis?.statedTotal?.value ?? null;
  const squares = result?.analysis?.roofSizeSquares?.value ?? null;

  return (
    <div className="min-h-screen bg-[#f7f9fc]">
      <Helmet>
        <title>Free Quote Check — Have Any Roofing Quote Analyzed | OpenbooQ</title>
        <meta
          name="description"
          content="Upload any roofing quote and get a free Quote Check: we re-run the math, check the 12 essentials every quote should state, flag what's worth asking about, and compare the price against your local market. A fair quote gets told it's fair."
        />
        <meta property="og:title" content="Free Quote Check — Have Any Roofing Quote Analyzed | OpenbooQ" />
        <meta
          property="og:description"
          content="Is your roofing quote fair? Upload it and find out — we re-run the math, check the 12 essentials, and compare it against your local market. Free, no strings."
        />
      </Helmet>

      <Header />

      {/* ===== HERO (dark) ===== */}
      <section className="relative overflow-hidden border-b border-white/10 bg-[#0a1628] pt-44 pb-16 text-white sm:pt-40 lg:pt-48 lg:pb-20">
        {/* brand glow */}
        <div
          className="pointer-events-none absolute inset-0"
          aria-hidden="true"
          style={{
            background:
              'radial-gradient(ellipse 80% 60% at 15% 20%, rgba(7,176,233,0.18) 0%, transparent 60%), radial-gradient(ellipse 60% 50% at 85% 85%, rgba(7,176,233,0.12) 0%, transparent 60%)',
          }}
        />
        {/* grid lines */}
        <div
          className="pointer-events-none absolute inset-0"
          aria-hidden="true"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
            maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
          }}
        />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-[820px] text-center">
            <Reveal>
              <span className="inline-flex min-h-[28px] items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[#6cc7ff]">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                Free Quote Check
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mx-auto mt-5 mb-4 text-[clamp(32px,4.8vw,56px)] font-extrabold leading-[1.05] tracking-[-0.025em]">
                Got a roofing quote?{' '}
                <span className="text-[#6cc7ff]">Let's check it — free.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mx-auto max-w-[620px] text-[clamp(15px,1.3vw,17px)] leading-[1.65] text-white/80">
                Upload any roofing quote and we'll{' '}
                <strong className="font-semibold text-white">
                  re-run the math, check the 12 essentials every quote should state, and compare
                  the price against your local market
                </strong>
                . No account, no strings — and if it's a fair quote, we'll tell you that too.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===== UPLOAD ===== */}
      <section className="pt-12 pb-20 lg:pt-16 lg:pb-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl">
            <Reveal>
              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-trust">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-5 sm:px-7">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] border border-slate-200 bg-slate-50 text-slate-600">
                      <FileText size={20} aria-hidden="true" />
                    </div>
                    <div>
                      <div className="text-[17px] font-extrabold leading-tight tracking-tight text-[#0a1628]">
                        Your roofing quote
                      </div>
                      <div className="mt-0.5 max-w-[220px] truncate text-xs font-semibold uppercase tracking-wider text-slate-500 sm:max-w-[320px]">
                        {file ? file.name : 'PDF, JPG, or PNG · max 10 MB'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-5 sm:p-7">
                  {/* Empty: dropzone */}
                  {phase === 'empty' && (
                    <label
                      htmlFor="qc-file-input"
                      onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        handleFile(e.dataTransfer?.files?.[0]);
                      }}
                      className={`flex min-h-[280px] cursor-pointer flex-col items-center justify-center rounded-2xl border-2 px-6 py-10 text-center transition-colors duration-200 sm:px-8 ${
                        dragOver
                          ? 'border-solid border-primary bg-cyan-50/60'
                          : 'border-dashed border-slate-300 bg-[#f7f9fc] hover:border-primary hover:bg-cyan-50/60'
                      }`}
                    >
                      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-slate-200 bg-white text-[#0a6eb8]">
                        <Upload size={30} strokeWidth={1.6} aria-hidden="true" />
                      </div>
                      <h3 className="mb-2 text-lg font-extrabold tracking-tight text-[#0a1628]">
                        Drop your quote here
                      </h3>
                      <p className="mb-5 max-w-[380px] text-sm leading-relaxed text-slate-600">
                        Or click to browse. A photo of the paper quote works — we'll read it and
                        run the full checkup.
                      </p>
                      <span className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-[#0a1628] px-7 py-3 text-[15px] font-semibold text-white transition-colors duration-200 hover:bg-[#0a6eb8]">
                        Choose File
                        <ArrowRight size={18} aria-hidden="true" />
                      </span>
                      <span className="mt-4 inline-flex items-center gap-2 text-xs text-slate-500">
                        <Lock size={13} aria-hidden="true" />
                        PDF, JPG, or PNG · Max 10 MB · Stays private to you
                      </span>
                      <input
                        ref={fileInputRef}
                        id="qc-file-input"
                        type="file"
                        accept="application/pdf,image/jpeg,image/png,image/jpg"
                        className="hidden"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                      />
                    </label>
                  )}

                  {/* Uploaded: file pill + address + analyze */}
                  {phase === 'uploaded' && file && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                      <div className="mb-4 flex items-center gap-3.5 rounded-xl border border-slate-200 bg-[#f7f9fc] px-4 py-3.5">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[10px] border border-primary/30 bg-primary/10 text-[#0a6eb8]">
                          <FileText size={20} aria-hidden="true" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-bold text-[#0a1628]">{file.name}</div>
                          <div className="mt-0.5 text-xs text-slate-500">
                            {fmtBytes(file.size)} · {file.type === 'application/pdf' ? 'PDF' : 'Image'}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={clearFile}
                          aria-label="Remove file"
                          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-[10px] border border-slate-200 bg-white text-slate-500 transition-colors duration-200 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                        >
                          <X size={18} aria-hidden="true" />
                        </button>
                      </div>

                      {/* Optional address — unlocks the local market check.
                          Degrades gracefully: no address (or no satellite
                          coverage) still returns the math + essentials
                          checkup. */}
                      <div className="mb-4">
                        <label
                          htmlFor="qc-address"
                          className="mb-1 flex items-center gap-1.5 text-[12px] font-bold text-[#0a1628]"
                        >
                          <MapPin size={13} className="text-[#0a6eb8]" aria-hidden="true" />
                          Where's the roof?{' '}
                          <span className="font-medium text-slate-500">(optional)</span>
                        </label>
                        <input
                          id="qc-address"
                          type="text"
                          autoComplete="street-address"
                          placeholder="123 Main St, Columbus, OH"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          className="min-h-[48px] w-full rounded-xl border border-slate-300 bg-white px-4 text-[15px] text-[#0a1628] placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                        />
                        <p className="mt-1 text-xs text-slate-500">
                          Your address unlocks the local market check.
                        </p>
                      </div>

                      <div className="flex flex-col gap-2.5">
                        <button
                          type="button"
                          onClick={analyze}
                          className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-[#0a1628] px-7 py-3 text-[15px] font-semibold text-white transition-colors duration-200 hover:bg-[#0a6eb8]"
                        >
                          Run my free Quote Check
                          <ArrowRight size={18} aria-hidden="true" />
                        </button>
                        <small className="text-center text-xs text-slate-500">
                          Usually under a minute. The full report renders right here — no email
                          required to see it.
                        </small>
                      </div>
                    </div>
                  )}

                  {/* Analyzing */}
                  {phase === 'analyzing' && (
                    <div className="px-4 py-10 text-center" role="status" aria-live="polite">
                      <div
                        className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-[3px] border-slate-200 border-t-primary motion-reduce:animate-none"
                        aria-hidden="true"
                      />
                      <h4 className="mb-1.5 text-base font-extrabold text-[#0a1628]">
                        Running your Quote Check…
                      </h4>
                      <p className="text-[13px] text-slate-600">{statuses[statusIdx]}</p>
                    </div>
                  )}

                  {/* Error banner — ref'd so validation/API failures scroll
                      into view on phones (QA W1.1: silent-rejection fix). */}
                  {error && (
                    <div
                      ref={errorRef}
                      className="mt-4 flex items-start gap-2.5 rounded-2xl border border-red-300 bg-red-50 px-4 py-4 text-sm leading-relaxed text-red-700"
                      role="alert"
                    >
                      <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>{error}</span>
                    </div>
                  )}
                </div>
              </div>
            </Reveal>
          </div>

          {/* ===== THE REPORT CARD ===== */}
          {result && (
            <div ref={resultRef} className="mx-auto mt-8 max-w-2xl scroll-mt-32">
              <VerdictBanner
                verdict={result.verdict}
                headline={result.verdictHeadline}
                detail={result.verdictDetail}
              />

              <CheckupStrip checkup={result.checkup} verdict={result.verdict} />

              {/* Extraction context — the ONLY place the contractor name
                  renders (never inside a verdict sentence). */}
              <p className="mt-3 mb-1 px-1 text-[12.5px] leading-relaxed text-slate-500">
                From the document: {contractorName}
                {statedTotal != null && <> · total as printed {fmtMoney(statedTotal)}</>}
                {squares != null && <> · {squares} squares</>}
                {result.analysis.scopeSummary && <> — {result.analysis.scopeSummary}</>}
              </p>

              <div className="mt-4 space-y-4">
                <Reveal>
                  <MathCard math={result.checkup.math} />
                </Reveal>
                <Reveal>
                  <EssentialsCard essentials={result.checkup.essentials} />
                </Reveal>
                <Reveal>
                  <PriceCard
                    benchmark={result.benchmark}
                    total={statedTotal}
                    verdict={result.verdict}
                  />
                </Reveal>
                <Reveal>
                  <FlagsCard redFlags={result.checkup.redFlags} />
                </Reveal>
                <Reveal>
                  <PositivesCard positives={result.checkup.positives} />
                </Reveal>
                {/* Share card (PRD §5.9): sits at the delight peak, right
                    after "what this quote gets right". The shared view is
                    redacted server-side — contractor name hidden. */}
                <Reveal>
                  <ShareCard
                    runId={result.runId}
                    city={result.benchmark.metro || result.benchmark.state}
                    pushDataLayer={pushDataLayer}
                  />
                </Reveal>
                <Reveal>
                  <NudgeCard address={address} />
                </Reveal>
                <Reveal>
                  <ClaimCard runId={result.runId} pushDataLayer={pushDataLayer} />
                </Reveal>
                <DisclaimerFooter />
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CompareQuotePage;
